import React, { useEffect, useRef, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import {
  watchInstallPrompt,
  fireInstallPrompt,
  isInstallDismissed,
  dismissInstall,
  isStandalone,
} from "@/lib/pwa";
import { capture } from "@/lib/analytics";

/**
 * Smart "Add to Home Screen" affordance.
 * - Hidden when already installed (standalone) or recently dismissed.
 * - Chrome / Edge / Android: listens for `beforeinstallprompt` → native install
 * - iOS Safari: tapping the card opens a step-by-step instruction sheet
 *   (no native install API exists on iOS — Apple gates this behind Safari's
 *   Share menu, so we must guide the user).
 *
 * Analytics events (PostHog):
 *   install_banner_shown          — once per banner appearance
 *   install_card_clicked          — user taps the card
 *   install_native_prompt_result  — native prompt resolved (accepted/dismissed)
 *   install_ios_sheet_opened      — iOS Safari user opens the 3-step sheet
 *   install_dismissed             — soft X dismiss
 *   install_dismissed_forever     — "Don't show again" in iOS sheet
 */
export default function InstallPrompt() {
  const [canInstall, setCanInstall] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [iosSheetOpen, setIosSheetOpen] = useState(false);
  const shownRef = useRef(false);

  useEffect(() => {
    if (isStandalone() || isInstallDismissed()) {
      setHidden(true);
      return;
    }
    const off = watchInstallPrompt((available) => setCanInstall(!!available));
    const ua = navigator.userAgent || "";
    const isIOS = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|crios|fxios).)*safari/i.test(ua);
    if (isIOS && isSafari) setIosHint(true);
    return off;
  }, []);

  // Fire install_banner_shown exactly once per page-life when the banner becomes visible.
  useEffect(() => {
    if (hidden || shownRef.current) return;
    if (!canInstall && !iosHint) return;
    shownRef.current = true;
    capture("install_banner_shown", {
      platform: iosHint ? "ios_safari" : (canInstall ? "native" : "unknown"),
    });
  }, [hidden, canInstall, iosHint]);

  if (hidden) return null;
  if (!canInstall && !iosHint) return null;

  const handleInstall = async () => {
    capture("install_card_clicked", {
      platform: iosHint ? "ios_safari" : (canInstall ? "native" : "unknown"),
    });
    if (canInstall) {
      const choice = await fireInstallPrompt();
      capture("install_native_prompt_result", { outcome: choice?.outcome || "unknown" });
      if (choice?.outcome === "accepted") setHidden(true);
      else if (choice?.outcome === "dismissed") {
        dismissInstall();
        setHidden(true);
      }
    } else if (iosHint) {
      capture("install_ios_sheet_opened");
      setIosSheetOpen(true);
    }
  };

  const close = (e) => {
    e?.stopPropagation();
    capture("install_dismissed", {
      platform: iosHint ? "ios_safari" : (canInstall ? "native" : "unknown"),
    });
    dismissInstall();
    setHidden(true);
  };

  return (
    <>
      <div
        className="fixed left-4 right-4 md:left-auto md:right-6 md:bottom-6 bottom-20 z-50 max-w-sm md:ml-auto"
        data-testid="pwa-install-prompt"
      >
        {/* Whole card is now an interactive button — solves the "nothing happens" bug. */}
        <button
          type="button"
          onClick={handleInstall}
          className="w-full text-left rounded-2xl border border-[var(--line)] bg-[var(--ink)] text-white p-4 shadow-2xl flex items-start gap-3 hover:opacity-95 active:scale-[0.99] transition"
          style={{ background: "linear-gradient(135deg, #0F1419 0%, #1E293B 100%)" }}
          data-testid="pwa-install-card"
          aria-label={iosHint && !canInstall ? "Show install instructions" : "Install PromptDojo"}
        >
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "var(--emerald, #10B981)" }}
          >
            <Download size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">Install PromptDojo</div>
            <p className="text-xs text-white/70 mt-1 leading-snug">
              {iosHint && !canInstall
                ? "Tap here to see how to add the app to your home screen."
                : "Add PromptDojo to your home screen. Streak reminders + offline lessons."}
            </p>
            <span
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold bg-[var(--emerald,#10B981)] text-white rounded-full px-3 py-1.5"
              data-testid="pwa-install-button"
            >
              <Download size={12} /> {iosHint && !canInstall ? "Show me how" : "Install"}
            </span>
          </div>
          <span
            onClick={close}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") close(e); }}
            aria-label="Dismiss install prompt"
            className="text-white/50 hover:text-white/90 flex-shrink-0 inline-flex items-center justify-center w-10 h-10 -mr-2 -mt-2 rounded-lg"
            data-testid="pwa-install-dismiss"
          >
            <X size={18} />
          </span>
        </button>
      </div>

      {/* ── iOS install instructions sheet ──────────────────────────── */}
      {iosSheetOpen && (
        <>
          <div
            className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-sm"
            onClick={() => setIosSheetOpen(false)}
            data-testid="ios-install-backdrop"
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="ios-install-title"
            className="fixed left-0 right-0 bottom-0 z-[70] bg-white rounded-t-3xl shadow-2xl safe-bottom"
            data-testid="ios-install-sheet"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)" }}
          >
            <div className="mx-auto w-12 h-1.5 rounded-full bg-[var(--line-strong)] my-3" />
            <div className="px-6 pb-2">
              <h3 id="ios-install-title" className="font-display text-2xl tracking-tight text-[var(--ink)]">Add to Home Screen</h3>
              <p className="text-sm text-[var(--ink-3)] mt-1">3 quick steps in Safari</p>
            </div>
            <ol className="px-6 mt-4 space-y-4">
              <li className="flex items-start gap-3" data-testid="ios-step-1">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--ink)] text-white font-bold text-sm flex items-center justify-center">1</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[var(--ink)]">Tap the Share button</div>
                  <p className="text-xs text-[var(--ink-3)] mt-1 leading-snug flex items-center gap-2">
                    Look for
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-[var(--line-strong)] bg-[var(--bg-2)]">
                      <Share size={14} className="text-[var(--ink-2)]" />
                    </span>
                    at the bottom of Safari.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3" data-testid="ios-step-2">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--ink)] text-white font-bold text-sm flex items-center justify-center">2</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[var(--ink)]">Scroll and choose "Add to Home Screen"</div>
                  <p className="text-xs text-[var(--ink-3)] mt-1 leading-snug flex items-center gap-2">
                    The
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-[var(--line-strong)] bg-[var(--bg-2)]">
                      <Plus size={14} className="text-[var(--ink-2)]" />
                    </span>
                    icon in the share menu.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3" data-testid="ios-step-3">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--ink)] text-white font-bold text-sm flex items-center justify-center">3</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[var(--ink)]">Tap "Add" in the top-right</div>
                  <p className="text-xs text-[var(--ink-3)] mt-1 leading-snug">PromptDojo will appear on your home screen like a real app.</p>
                </div>
              </li>
            </ol>
            <div className="px-6 mt-6 flex flex-col gap-2">
              <button
                onClick={() => setIosSheetOpen(false)}
                className="btn-primary w-full inline-flex items-center justify-center min-h-[48px]"
                data-testid="ios-install-sheet-got-it"
              >
                Got it
              </button>
              <button
                onClick={() => { capture("install_dismissed_forever"); dismissInstall(); setIosSheetOpen(false); setHidden(true); }}
                className="text-xs text-[var(--ink-3)] hover:text-[var(--ink-2)] py-2"
                data-testid="ios-install-sheet-dismiss-forever"
              >
                Don't show this again
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
